import { Col, Form, Row } from "antd";
import { EMatButton, EMatFormItem, EMatInput } from "Components";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { validatePriceFrom, validatePriceTo, validateQuantity } from "src/common/orderValidations";
import EMAT_INPUT_TYPES from "src/constants/EMatInputTypes";
import { OPERATION_POSITION, OPTION_TYPES } from "src/constants/operation";
import "../../BuyOrSellModal/BuyOrSellForm/BuyOrSellForm.scss";

const BuyOrSellForm = ({
  instrument,
  price,
  onCancel,
  onSubmit,
  id,
  loading,
  isBuy,
  operationToEdit,
  optionType,
}: {
  instrument: string;
  price: number;
  onCancel: () => void;
  onSubmit: (values: any) => void;
  id: string;
  loading: boolean;
  isBuy: boolean;
  operationToEdit: any;
  optionType: string;
}) => {
  const [t] = useTranslation();
  const [form] = Form.useForm();
  const [priceDisabled, setPriceDisabled] = useState(false);
  const [switchChecked, setSwitchChecked] = useState(false);

  const validatePrice = (rule: any, value: any) => {
    try {
      if (priceDisabled) return Promise.resolve();
      const intValue = parseFloat(value);
      if (intValue <= 0)
        return Promise.reject(new Error(t("common:errors.biggerThanValue", { min: "0" })));
    } catch (error) {
      return Promise.reject(
        new Error(
          t("common:errors.invalidValue", {
            field:
              optionType === OPTION_TYPES.FUTURE
                ? t("operation:futures.price")
                : t("operation:spreads.price"),
          })
        )
      );
    }
    return Promise.resolve();
  };

  const handleToMarket = (e: any) => {
    if (e.target.checked) {
      setPriceDisabled(true);
      form.resetFields(["price", "priceTo", "priceFrom"]);
      form.setFieldsValue({
        price: undefined,
        priceTo: undefined,
        priceFrom: undefined,
      });
      form.setFieldsValue({ toMarket: 1 });
    } else {
      setPriceDisabled(false);
      form.setFieldsValue({ toMarket: 0 });
    }
  };

  const handleSwitch = (value: any) => {
    setSwitchChecked(value);
    form.setFieldsValue({
      position: value ? OPERATION_POSITION.CLOSE : OPERATION_POSITION.OPEN,
    });
  };

  const initialValues = operationToEdit
    ? {
        id: operationToEdit.id,
        optionType: operationToEdit.option_type,
        quantity: operationToEdit.quantity,
        price: operationToEdit.price,
        priceFrom: operationToEdit.price_from,
        priceTo: operationToEdit.price_to,
        toMarket: !!operationToEdit.to_market,
        position: operationToEdit.position ? OPERATION_POSITION.CLOSE : OPERATION_POSITION.OPEN,
      }
    : {
        position: OPERATION_POSITION.OPEN,
        price: price ? price : undefined,
        toMarket: 0,
      };

  useEffect(() => {
    if (operationToEdit) {
      if (!!operationToEdit.to_market === true) setPriceDisabled(true);
      if (operationToEdit.position === OPERATION_POSITION.CLOSE) {
        form.setFieldsValue({ position: OPERATION_POSITION.CLOSE });
        setSwitchChecked(true);
      } else {
        form.setFieldsValue({ position: OPERATION_POSITION.OPEN });
      }
    }
  }, [operationToEdit]);

  return (
    <>
      <div className="instrument">
        <h3 id="instrument">{instrument}</h3>
      </div>
      <Form
        id={id}
        form={form}
        onFinish={onSubmit}
        onFinishFailed={(error) => console.log(error)}
        initialValues={initialValues}
      >
        <Row gutter={8}>
          <Col sm={12} xs={24}>
            <EMatFormItem
              name="price"
              rules={[
                {
                  required: !priceDisabled,
                  message: t("common:errors:fieldRequiredDefault"),
                },
                { validator: validatePrice },
              ]}
              validateTrigger={["onBlur"]}
            >
              <EMatInput
                min="0"
                step=".01"
                disabled={priceDisabled}
                eMatType={EMAT_INPUT_TYPES.NUMBER}
                id="price"
                // placeholder={t("operation:futures.price")}
                placeholder={
                  optionType === OPTION_TYPES.FUTURE
                    ? t("operation:futures.price")
                    : t("operation:spreads.price")
                }
                suffix="$"
              />
            </EMatFormItem>
          </Col>
          <Col sm={12} xs={24} className="col-to-market">
            <EMatFormItem name="toMarket" valuePropName="checked">
              <EMatInput
                eMatType={EMAT_INPUT_TYPES.CHECKBOX}
                id="price-to-Market"
                onChange={handleToMarket}
              >
                {t("operation:common.to-market")}
              </EMatInput>
            </EMatFormItem>
          </Col>
        </Row>
        <Row gutter={8}>
          <Col sm={12} xs={24}>
            <EMatFormItem
              name="priceFrom"
              rules={[
                { validator: (rule, value) => validatePriceFrom(rule, value, t, form, optionType) },
              ]}
              validateTrigger={["onBlur"]}
            >
              <EMatInput
                min="0"
                step=".01"
                disabled={priceDisabled}
                eMatType={EMAT_INPUT_TYPES.NUMBER}
                id="price-from"
                placeholder={t("operation:common.priceFrom")}
                suffix="$"
              />
            </EMatFormItem>
          </Col>
          <Col sm={12} xs={24}>
            <EMatFormItem
              name="priceTo"
              rules={[
                {
                  required: !priceDisabled,
                  message: t("common:errors:fieldRequiredDefault"),
                },
                { validator: (rule, value) => validatePriceTo(rule, value, t, form) },
              ]}
              validateTrigger={["onBlur"]}
            >
              <EMatInput
                min="0"
                step=".01"
                disabled={priceDisabled}
                eMatType={EMAT_INPUT_TYPES.NUMBER}
                id="price-from"
                placeholder={t("operation:common.priceTo")}
                suffix="$"
              />
            </EMatFormItem>
          </Col>
        </Row>
        <EMatFormItem className="open-close-switch" name="position" valuePropName="checked">
          <span className="option">{t("operation:common.open")}</span>
          <EMatInput
            onChange={handleSwitch}
            eMatType={EMAT_INPUT_TYPES.SWITCH}
            id="open-close-switch"
            checked={switchChecked}
          />
          <span className="option">{t("operation:common.close")}</span>
        </EMatFormItem>
        <EMatFormItem className="buttons-container">
          <EMatButton type="text" id="cancel" onClick={onCancel}>
            {t("common:actions.cancel")}
          </EMatButton>
          <EMatButton type="primary" id="submit" htmlType="submit" loading={loading}>
            {t("common:actions.accept")}
          </EMatButton>
        </EMatFormItem>
      </Form>
    </>
  );
};

export default BuyOrSellForm;
